import { useEditorState } from "@/context/EditorStateContext";
import { Excalidraw } from "@excalidraw/excalidraw";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { ExcalidrawBinding, yjsToExcalidraw } from "y-excalidraw";
import * as Y from "yjs";

export default function ExcalidrawPage() {
	const [api, setApi] = useState<ExcalidrawImperativeAPI | null>(null);
	const [binding, setBindings] = useState<ExcalidrawBinding | null>(null);
	const excalidrawRef = useRef(null);

	const { ydoc, awareness } = useEditorState();
	const yElements = useMemo(
		() => ydoc.getArray<Y.Map<unknown>>("elements"),
		[ydoc],
	);
	const yAssets = useMemo(() => ydoc.getMap("assets"), [ydoc]);

	useEffect(() => {
		if (!api || !excalidrawRef.current || !awareness) return;

		const binding = new ExcalidrawBinding(
			yElements,
			yAssets,
			api,
			awareness,
			// excalidraw dom is needed to override the undo/redo buttons in the UI as there is no way to override it via props in excalidraw
			// You might need to pass {trackedOrigins: new Set()} to undomanager depending on whether your provider sets an origin or not
			{
				excalidrawDom: excalidrawRef.current,
				undoManager: new Y.UndoManager(yElements),
			},
		);
		setBindings(binding);
		return () => {
			setBindings(null);
			binding.destroy();
		};
	}, [api, yAssets, yElements, awareness]);

	const initData = {
		elements: yjsToExcalidraw(yElements),
	};

	return (
		<div className="w-full h-full" ref={excalidrawRef}>
			<Excalidraw
				initialData={initData} // Need to set the initial data
				excalidrawAPI={setApi}
				onPointerUpdate={binding?.onPointerUpdate}
				theme="dark"
			/>
		</div>
	);
}
